import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";
import { Dashboard } from "@/pages/Dashboard";
import { Appearance } from "@/pages/Appearance";
import { Users } from "@/pages/Users";
import { Admins } from "@/pages/Admins";
import { FAQs } from "@/pages/FAQs";
import Content from "@/pages/Content";
import { Templates } from "@/pages/Templates";
import { Notifications } from "@/pages/Notifications";
import { Settings } from "@/pages/Settings";
import { Groups } from "@/pages/Groups";
import { Statistics } from "@/pages/Statistics";
import { TestRoute } from "@/pages/TestRoute";
import { SimpleTest } from "@/pages/SimpleTest";
import { Landing } from "@/pages/Landing";
import { Login } from "@/pages/auth/Login";
import { ForgotPassword } from "@/pages/auth/ForgotPassword";
import { VerifyOTP } from "@/pages/auth/VerifyOTP";
import { ResetPassword } from "@/pages/auth/ResetPassword";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { Establishments } from "@/pages/Establishments";
import { HelpSupport } from "@/pages/HelpSupport";
import { TicketDetail } from "@/pages/TicketDetail";
import SubscriptionPlans from "@/pages/SubscriptionPlans";
import UserProfileViewer from "@/pages/UserProfileViewer";
import EstablishmentDetails from "@/pages/EstablishmentDetails";
import RealTimeDashboard from "@/pages/RealTimeDashboard";
import PaymentProcessingDashboard from "@/pages/PaymentProcessingDashboard";
import AdvancedUserAnalytics from "@/pages/AdvancedUserAnalytics";
// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-background">
              <Routes>
                {/* Public Routes */}
                <Route path="/landing" element={<Landing />} />

                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/verify-otp" element={<VerifyOTP />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/appearance"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Appearance />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Users />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admins"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Admins />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/establishments"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Establishments />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Notifications />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Templates />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/groups"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Groups />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/content"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Content />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/faqs"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FAQs />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/help-support"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <HelpSupport />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/help-support/ticket/:ticketId"
                  element={
                    <ProtectedRoute>
                      <TicketDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription-plans"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SubscriptionPlans />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/real-time-dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <RealTimeDashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-processing"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <PaymentProcessingDashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-analytics"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <AdvancedUserAnalytics />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users/:userId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserProfileViewer />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/establishments/:establishmentId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EstablishmentDetails />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/statistics"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Statistics />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Settings />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/test"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <TestRoute />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route path="/simple" element={<SimpleTest />} />
                <Route path="*" element={<Navigate to="/landing" replace />} />
              </Routes>
              <Toaster />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
